import React, {
  useMemo,
  useState,
  useCallback,
  memo,
  useEffect
} from "react";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import formatUrlZoom from "../utils/formatUrlZoom";
import { useTraceUpdate } from "../hooks/shared/useDebugRenders";

const lightboxCustomStyles = {
  overlay: {
    zIndex: 11000
  },
  content: {
    left: "60px",
    zIndex: 11001
  }
};

/**
 * @fileoverview Lightbox genérico de imágenes navegables (lista ya resuelta por el caller).
 * @module GalleryLightboxMC
 * @category Component - Organism
 *
 * @typedef {Object} GalleryImageEntry
 * @property {string|number} id
 * @property {string} url
 * @property {string|number} [identifier]
 * @property {string} [caption]
 *
 * @typedef {Object} GalleryLightboxMCProps
 * @property {GalleryImageEntry[]} images - Imágenes en orden de navegación
 * @property {string|number} [selectedId] - Id (o identifier) de la imagen inicial
 * @property {function} [onSelectImage] - Callback al cambiar de imagen (id)
 * @property {function} onCloseRequest - Al cerrar el lightbox
 *
 * @param {GalleryLightboxMCProps} props
 * @returns {JSX.Element|null}
 */
const GalleryLightboxMC = memo(({
  images = [],
  selectedId,
  onSelectImage,
  onCloseRequest
}) => {
  const [photoIndex, setPhotoIndex] = useState(() => {
    if (!selectedId) return 0;
    const idx = images.findIndex((img) => img.id === selectedId || img.identifier === selectedId);
    return idx >= 0 ? idx : 0;
  });

  useEffect(() => {
    if (!images.length) return;
    if (photoIndex >= 0 && photoIndex < images.length && images[photoIndex]?.id) {
      const currentId = images[photoIndex].id || images[photoIndex].identifier;
      const stillExists = images.some((img) => img.id === currentId || img.identifier === currentId);
      if (stillExists) return;
    }
    if (selectedId) {
      const idx = images.findIndex((img) => img.id === selectedId || img.identifier === selectedId);
      if (idx !== -1) {
        setPhotoIndex(idx);
        return;
      }
    }
    setPhotoIndex(-1);
  }, [images, selectedId, photoIndex]);

  const handleSelectImage = useCallback(
    (id) => {
      const idx = images.findIndex((img) => img.id === id || img.identifier === id);
      if (idx !== -1) {
        setPhotoIndex(idx);
        onSelectImage?.(id);
      }
    },
    [images, onSelectImage]
  );

  const handlePrev = useCallback(() => {
    const prevIdx = (photoIndex + images.length - 1) % images.length;
    setPhotoIndex(prevIdx);
    handleSelectImage(images[prevIdx].id || images[prevIdx].identifier);
  }, [photoIndex, images, handleSelectImage]);

  const handleNext = useCallback(() => {
    const nextIdx = (photoIndex + 1) % images.length;
    setPhotoIndex(nextIdx);
    handleSelectImage(images[nextIdx].id || images[nextIdx].identifier);
  }, [photoIndex, images, handleSelectImage]);

  useTraceUpdate('GalleryLightboxMC', {
    selectedId,
    photoIndex,
    imagesLength: images?.length,
    currentImageId: images[photoIndex]?.id || images[photoIndex]?.identifier,
    currentImageUrl: images[photoIndex]?.url,
    lightboxVisible: images.length > 0 && photoIndex >= 0 && photoIndex < images.length
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  const noValidImage = !images.length || photoIndex < 0 || photoIndex >= images.length;

  const mainSrc = useMemo(() => {
    if (noValidImage) return undefined;
    return formatUrlZoom(images[photoIndex]?.url);
  }, [images, photoIndex, noValidImage]);
  const nextSrc = useMemo(() => {
    if (noValidImage) return undefined;
    return formatUrlZoom(images[(photoIndex + 1) % images.length]?.url);
  }, [images, photoIndex, noValidImage]);
  const prevSrc = useMemo(() => {
    if (noValidImage) return undefined;
    return formatUrlZoom(images[(photoIndex + images.length - 1) % images.length]?.url);
  }, [images, photoIndex, noValidImage]);
  const imageCaption = useMemo(() => {
    if (noValidImage) return undefined;
    return images[photoIndex]?.caption;
  }, [images, photoIndex, noValidImage]);

  if (noValidImage) {
    return null;
  }

  return (
    <Lightbox
      mainSrc={mainSrc}
      nextSrc={nextSrc}
      prevSrc={prevSrc}
      onCloseRequest={onCloseRequest}
      onMovePrevRequest={handlePrev}
      onMoveNextRequest={handleNext}
      imageLoadErrorMessage="Error al cargar la imagen"
      reactModalStyle={lightboxCustomStyles}
      imageCaption={imageCaption}
    />
  );
});

GalleryLightboxMC.displayName = "GalleryLightboxMC";

export default GalleryLightboxMC;
